import { cn } from '@/shared/utils';
import { useIntersectionObserver } from 'react-simplikit';
import { LazyLoader } from '../lazy-loader/lazy-loader';
import { RingSpinner } from '../loader/ring-spinner';
import { Scene, Stage } from '../stage/stage';
import type { ListProps } from './list-types';

export const List = <T,>(props: ListProps<T>) => {
  const { ref } = props;
  return (
    <Stage className={cn('scrollbar-hide flex flex-1 flex-col overflow-auto', props.className)}>
      <Scene status={props.isLoading} ref={ref}>
        {props.loader || (
          <LazyLoader
            show={props.isLoading}
            delay={props.lazyLoaderDelay}
            loader={
              <div className="flex-center h-full flex-1">
                <RingSpinner />
              </div>
            }
          />
        )}
      </Scene>
      <Scene ref={ref} data={props.data != null && props.data.length > 0 ? props.data : null}>
        {(data) => {
          return (
            <>
              {data.map(props.render)}
              {props.fetchNextPage && props.hasNextPage && !props.isFetchingNextPage && (
                <NextPageLoader fetchNextPage={props.fetchNextPage} />
              )}
              {props.isFetchingNextPage && (
                <div className="flex-center h-16">
                  <RingSpinner />
                </div>
              )}
            </>
          );
        }}
      </Scene>
      <Scene status={props.isError} ref={ref}>
        {props.errorDisplay?.node ? (
          props.errorDisplay.node
        ) : (
          <div className="flex-center h-full flex-1">
            <span className="text-muted-foreground">
              {props.errorDisplay?.message || 'Error occured'}
            </span>
          </div>
        )}
      </Scene>
      <Scene default ref={ref}>
        {props.placeholder?.node || (
          <div className="flex-center h-full flex-1">
            <span className="text-muted-foreground">{props.placeholder?.message || 'No data'}</span>
          </div>
        )}
      </Scene>
    </Stage>
  );
};

type LoaderProps = {
  fetchNextPage: () => void;
};

const NextPageLoader = (props: LoaderProps) => {
  const loaderRef = useIntersectionObserver(
    (entry) => {
      if (entry.isIntersecting) {
        props.fetchNextPage();
      }
    },
    { threshold: 0.5 },
  );

  return <div ref={loaderRef} style={{ width: '100%', height: '40px', flexShrink: 0 }} />;
};
